import * as React from 'react';

import { Button, Dropdown, Grid, Search } from 'semantic-ui-react';

import { localeData } from '../../locales/data';
import { convertArrayFromDropdownProperties } from '../../shared/helpers';

import './FilterMenu.scss';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { useCompetitionTeamsByPrefix } from '../../shared/api/apiHooks';
import { useDebounce } from 'use-debounce';
import { FilterValue } from '../../containers/Competitions/types';
import { useIntl } from 'react-intl';

interface FilterMenuProps {
  boardType?: string;
  competitionId?: string;
  filters: any;
  filterValues: any;
  filterType: string;
  handleChangeFilter: (e: any, data: FilterValue, filter?: string) => void;
  handleCloseFilter: (value: string) => void;
  handleApplyFilters?: () => void;
  handleResetFilters?: () => void;
  currentFilters?: any;
}

interface AutoCompleteSearchProps {
  boardType?: string;
  competitionId: string;
  onChange: (e: any, value: FilterValue) => void;
  onDeleteFilter: () => void;
  onBlur?: (e: any) => void;
  savedValue?: string;
  type: 'team' | 'user';
  boardRegion?: string;
}

interface ResultRendererProps {
  title: string;
}

function ResultRenderer({ title }: ResultRendererProps) {
  return <div className="item filters-competitions__autocomplete-option">{title}</div>;
}

interface OptionData {
  value?: string;
}

const minPrefixLength = 2;

export function AutoCompleteSearch({
  competitionId,
  boardType,
  onChange,
  onDeleteFilter,
  savedValue,
  type,
  boardRegion,
}: AutoCompleteSearchProps) {
  const [value, setValue] = React.useState(savedValue ?? '');
  const handleChange = (e: any, data: OptionData) => {
    setValue(data.value ?? '');
  };
  const saveValue = () => {
    onChange(null, { name: type, value });
    if (savedValue && value === '') {
      onDeleteFilter();
    }
  };

  interface SelectedAutocompleteValue {
    result: {
      title: string;
    };
  }

  const handleSelect = (e: any, data: SelectedAutocompleteValue) => {
    onChange(e, { name: type, value: data.result.title });
  };
  const handleDeleteFilter = () => {
    onDeleteFilter();
    setValue('');
  };
  const [debouncedValue] = useDebounce(value.toLowerCase(), 200);
  const intl = useIntl();

  const formattedBoardType = boardType === 'powerPool' ? 'power-pool' : boardType;

  const { data, isFetching } = useCompetitionTeamsByPrefix({
    competitionId: competitionId,
    prefix: debouncedValue,
    type: type,
    boardType: formattedBoardType,
    minPrefixLength,
    boardRegion,
  });
  const results = data?.map(item => ({
    title: item,
  }));
  return (
    <div className="filters-competitions__autocomplete-container">
      <Search
        className="filters__autocomplete-input search-custom"
        size="mini"
        placeholder={formatMessageHelper(intl, type === 'team' ? 'competitionMenu.filterMenu.team' : 'competitionMenu.filterMenu.user')}
        loading={isFetching}
        onResultSelect={handleSelect}
        onSearchChange={handleChange}
        resultRenderer={ResultRenderer}
        results={results}
        showNoResults={true}
        noResultsDescription={formatMessageHelper(
          intl,
          value?.length >= minPrefixLength ? 'competitionMenu.filterMenu.noResults' : 'competitionMenu.filterMenu.shortQuery'
        )}
        onBlur={saveValue}
        defaultValue={savedValue}
      />
      <div className={`filters-competitions__available-close`} onClick={handleDeleteFilter} />
    </div>
  );
}

const FilterMenu = ({
  boardType,
  competitionId,
  filters,
  filterValues,
  filterType,
  handleCloseFilter,
  handleChangeFilter,
  handleApplyFilters,
  handleResetFilters,
  currentFilters,
}: FilterMenuProps) => {
  const isCompetitions = filterType === 'competitions';

  return (
    <div className={`filters-${filterType}`}>
      <Grid className={`filters-${filterType}__available no-padding-horizontal`}>
        {filters &&
          filters.map((filter: any, index: number) => {
            const handleDeleteFilter = () => handleCloseFilter(filter.name);
            const handleOnChangeFilter = isCompetitions
              ? (_: any, data: any) => handleChangeFilter(_, data)
              : (_: any, data: any) => handleChangeFilter(_, data, filter.name);
            const options: any = isCompetitions
              ? filter.choices
                ? convertArrayFromDropdownProperties(filter.choices)
                : []
              : filter.choices;

            if (isCompetitions && competitionId && (filter.name === 'team' || filter.name === 'user')) {
              const boardRegion =
                boardType === 'powerPool' || boardType === 'spcLeaderboard'
                  ? filterValues['board'] ?? currentFilters.$board?.slice(6)
                  : undefined;

              return (
                <Grid.Column key={index} className={`filters-${filterType}__available-input`} computer={4} mobile={16} tablet={8}>
                  <AutoCompleteSearch
                    boardType={boardType}
                    onChange={handleOnChangeFilter}
                    competitionId={competitionId}
                    onDeleteFilter={handleDeleteFilter}
                    savedValue={filterValues[filter.name]}
                    type={filter.name}
                    boardRegion={boardRegion}
                  />
                </Grid.Column>
              );
            }
            return (
              <Grid.Column key={index} className={`filters-${filterType}__available-input`} computer={4} mobile={16} tablet={8}>
                <Dropdown
                  fluid={true}
                  search={true}
                  selection={true}
                  options={options}
                  value={filterValues[filter.name]}
                  onChange={handleOnChangeFilter}
                  name={filter.name}
                  placeholder={filter.label}
                  className="dropdown-custom"
                />
                <div className={`filters-${filterType}__available-close`} onClick={handleDeleteFilter} />
              </Grid.Column>
            );
          })}
      </Grid>
      {isCompetitions && (
        <div className={`filters-${filterType}__buttons`}>
          <Button className="button button--primary" onClick={handleApplyFilters}>
            {/* eslint-disable-next-line react/jsx-no-undef */}
            <FormattedMessageHelper
              tagName="span"
              id="competitionMenu.filterMenu.apply"
              defaultMessage={localeData.en['competitionMenu.filterMenu.apply']}
            />
          </Button>
          <Button className="button button--secondary" onClick={handleResetFilters}>
            <FormattedMessageHelper
              tagName="span"
              id="competitionMenu.filterMenu.reset"
              defaultMessage={localeData.en['competitionMenu.filterMenu.reset']}
            />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterMenu;
